import React, { useEffect, useMemo, useState } from "react";
import ReactDataGridBase from "@inovua/reactdatagrid-community";
import "@inovua/reactdatagrid-community/index.css";
import "@inovua/reactdatagrid-community/base.css";
import "../../../theme/grid-dark-theme.scss";
import "../../../theme/grid-override.scss";
import useColorMode from "../../../hooks/useColorMode.tsx";
import { Translations } from "../../../enum/translations.enum.ts";
import { translatePolicyKey } from "../../PolicyDetails/PolicyDetails.tsx";
import logoUrl from "../../../images/logo/logo-192.png";

const SEPARATOR = ",";
const ReactDataGrid = (ReactDataGridBase as any).default ?? ReactDataGridBase;

interface DataGridProps {
  columns: any;
  dataSource: string[] | object[];
  isLoading: boolean;
  gridTitle: string;
  idProperty: string;
  editable?: boolean;
  onEditComplete?: (e: any) => void;
  onAdd?: () => void;
  onRowClick?: (row: any) => void;
  onDelete?: () => void;
  shouldShowAddAndDeleteButtons?: boolean;
  csvFilename?: string;
  icon?: string;
  onExportCSV?: () => void;
  getPrintRows?: (filteredRows: any[]) => any[];
  printReportMetadata?: {
    label: string;
    value: string;
  }[];
  printReportSummary?: string[];
  printReportFooterMetadata?: {
    label: string;
    value: string;
  }[];
}

const i18n = Object.assign({}, ReactDataGrid.defaultProps?.i18n ?? {}, {
  sortAsc: Translations.ASCENDING,
  sortDesc: Translations.DESCENDING,
  clear: Translations.CLEAR,
  clearAll: Translations.CLEAR_ALL,
  unsort: Translations.UNSORT,
  group: Translations.GROUP,
  ungroup: Translations.UNGROUP,
  lockStart: Translations.LOCK_START,
  lockEnd: Translations.LOCK_END,
  unlock: Translations.UNLOCK,
  columns: Translations.COLUMNS,
  autosizeToFit: Translations.AUTOSIZE_TO_FIT,
  autoresizeThisColumn: Translations.AUTORESIZE_THIS_COLUMN,
  autoresizeAllColumns: Translations.AUTORESIZE_ALL_COLUMNS,
  showFilteringRow: Translations.SHOW_FILTERING_ROW,
  hideFilteringRow: Translations.HIDE_FILTERING_ROW,
  contains: Translations.CONTAINS,
  notContains: Translations.DOES_NOT_CONTAIN,
  eq: Translations.EQUALS,
  neq: Translations.DOES_NOT_EQUAL,
  empty: Translations.EMPTY,
  notEmpty: Translations.NOT_EMPTY,
  startsWith: Translations.STARTS_WITH,
  endsWith: Translations.ENDS_WITH,
  enable: Translations.ENABLE,
  disable: Translations.DISABLE,
  perPageText: Translations.PER_PAGE,
  showingText: Translations.SHOWING_TEXT,
  pageText: Translations.PAGE,
  ofText: Translations.OF_TEXT,
  loading: Translations.LOADING,
  pageSize_10: "10",
  pageSize_100: "100",
  pageSize_10000: "Всички",
});

const parseSortableDate = (value: any): number => {
  if (!value) return 0;

  if (value instanceof Date) {
    return value.getTime();
  }

  const stringValue = String(value).trim();

  if (/^\d{8}$/.test(stringValue)) {
    const year = Number(stringValue.slice(0, 4));
    const month = Number(stringValue.slice(4, 6)) - 1;
    const day = Number(stringValue.slice(6, 8));

    return new Date(year, month, day).getTime();
  }

  // UI format: 15/06/2026
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(stringValue)) {
    const [day, month, year] = stringValue.split("/").map(Number);

    return new Date(year, month - 1, day).getTime();
  }

  const parsed = new Date(stringValue).getTime();

  return Number.isNaN(parsed) ? 0 : parsed;
};

const isDateColumn = (col: any) =>
  col.type === "date" || col.type === "datetime" || col.sortAsDate;

const DataGrid: React.FC<DataGridProps> = ({
  columns,
  dataSource,
  isLoading,
  gridTitle,
  idProperty,
  editable = false,
  onEditComplete,
  onAdd,
  onDelete,
  onRowClick = () => {},
  shouldShowAddAndDeleteButtons = false,
  csvFilename = "grid-data.csv",
  onExportCSV,
  getPrintRows,
  printReportMetadata,
  printReportFooterMetadata,
}) => {
  const [theme, setTheme] = useState("default-light");
  const [filterText, setFilterText] = useState("");
  const [sortInfo, setSortInfo] = useState<any[]>([]);
  const [colorMode] = useColorMode();
  const [gridRef, setGridRef] = useState(null);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [gridHeight, setGridHeight] = useState(0);

  useEffect(() => {
    const handleCtrlF = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        setIsSearchVisible((prev) => {
          if (prev) setFilterText("");
          return !prev;
        });
        setTimeout(() => {
          const input = document.querySelector<HTMLInputElement>(
            ".inovua-grid-small input[placeholder='Филтрирай...']"
          );
          input?.focus();
        }, 50);
      }
    };
    window.addEventListener("keydown", handleCtrlF);
    return () => window.removeEventListener("keydown", handleCtrlF);
  }, []);

  const withTooltip = (col: any) => {
    if (col.name === "selected" || col.name === "__rowNumber__") return col;
    const originalRender = col.render;
    return {
      ...col,
      cellDOMProps: ({ value }: { value: any }) => ({
        title: value === undefined || value === null ? "" : String(value),
      }),
      render: (cellProps: any) => {
        const safe =
          cellProps.value === undefined || cellProps.value === null
            ? ""
            : String(cellProps.value);
        if (originalRender) {
          const rendered = originalRender(cellProps);
          return <div title={safe}>{rendered}</div>;
        }
        return (
          <div
            style={{
              userSelect: "text",
              cursor: "text",
              fontWeight: 700,
              fontSize: "12px",
            }}
            title={safe}
          >
            {safe}
          </div>
        );
      },
    };
  };

  const rowNumberColumn = {
    name: "__rowNumber__",
    header: "№",
    sortable: false,
    filterable: false,
    resizable: false,
    enableColumnFilterContextMenu: false,
    maxWidth: 45,
    minWidth: 45,
    defaultWidth: 45,
    headerProps: {
      style: {
        fontSize: "12px",
        fontWeight: 700,
        padding: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center" as const,
        flexDirection: "column",
      },
    },
    render: ({ rowIndex }: { rowIndex: number }) => (
      <div
        style={{
          userSelect: "none" as const,
          cursor: "default",
          fontSize: "12px",
          color: "#8a8a8a",
          textAlign: "center" as const,
          fontWeight: 700,
        }}
      >
        {rowIndex + 1}
      </div>
    ),
  };

  const selectableColumns = [
    rowNumberColumn,
    ...columns
      .map((col, index, arr) => {
        const isLastCol = index === arr.length - 1;
        const hasExplicitFlex = !!col.flex;
        const hasExplicitWidth = !!col.defaultWidth;
        const shouldSortAsDate = isDateColumn(col);

        return {
          ...col,

          ...(shouldSortAsDate
            ? {
                sort: (a: any, b: any) => {
                  const aValue = a?.[col.name] ?? a;
                  const bValue = b?.[col.name] ?? b;
                  return parseSortableDate(aValue) - parseSortableDate(bValue);
                },
              }
            : {}),

          ...(isLastCol && !hasExplicitWidth
            ? { flex: 1 }
            : hasExplicitFlex
            ? { flex: col.flex }
            : { defaultWidth: hasExplicitWidth ? col.defaultWidth : 120 }),

          minWidth: col.minWidth ?? 80,
          resizable: true,
          headerProps: {
            style: { fontSize: "12px", fontWeight: 700, padding: "0 0 0 2px" },
          },
        };
      })
      .map(withTooltip),
  ];

  useEffect(() => {
    setTheme(colorMode === "dark" ? "grid-dark-theme" : "default-light");
  }, [colorMode]);

  useEffect(() => {
    const rowHeight = 28;
    const headerHeight = 30;
    const footerBuffer = 40;
    const calculatedHeight =
      filteredDataSource.length * rowHeight + headerHeight + footerBuffer;
    const maxHeight = Math.floor(window.innerHeight * 0.7);
    setGridHeight(Math.min(Math.max(calculatedHeight, 100), maxHeight));
  }, [dataSource]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      document
        .querySelectorAll(
          ".inovua-react-toolkit-combo-box__value__display-value"
        )
        .forEach((el) => {
          if (el.textContent === "10000") el.textContent = "всички";
        });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isFullscreen) setIsFullscreen(false);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isFullscreen]);

  const filteredDataSource = useMemo(() => {
    let data = dataSource.filter((item: any) =>
      Object.values(item).some((value) =>
        String(value).toLowerCase().includes(filterText.toLowerCase())
      )
    );

    if (sortInfo.length === 0) return data;

    return [...data].sort((a: any, b: any) => {
      for (const sort of sortInfo) {
        const col = columns.find(
          (c: any) => c.name === sort.name || c.id === sort.name
        );
        let aVal = a[sort.name] ?? "";
        let bVal = b[sort.name] ?? "";

        let result = 0;

        if (col?.sort) {
          result = col.sort(aVal, bVal);
        } else if (col && isDateColumn(col)) {
          result = parseSortableDate(aVal) - parseSortableDate(bVal);
        } else {
          const aNum = parseFloat(String(aVal).replace(",", "."));
          const bNum = parseFloat(String(bVal).replace(",", "."));
          const bothNumeric =
            !isNaN(aNum) &&
            !isNaN(bNum) &&
            String(aVal).trim() !== "" &&
            String(bVal).trim() !== "";
          if (bothNumeric) {
            result = aNum - bNum;
          } else {
            result = String(aVal).localeCompare(String(bVal), "bg", {
              numeric: true,
              sensitivity: "base",
            });
          }
        }

        if (result !== 0) return sort.dir === -1 ? -result : result;
      }
      return 0;
    });
  }, [dataSource, filterText, sortInfo, columns]);

  const exportCSV = () => {
    const currentColumns = gridRef.current.visibleColumns.filter(
      (c) => c.name !== "selected" && c.name !== "__rowNumber__"
    );
    const filteredRows = gridRef.current?.data || filteredDataSource;
    const rowsToExport = getPrintRows
      ? getPrintRows(filteredRows)
      : filteredRows;

    const metadataRows = (printReportMetadata ?? []).map(
      (item) => `${item.label}${SEPARATOR}${item.value}`
    );

    const footerRows = (printReportFooterMetadata ?? []).map(
      (item) => `${item.label}${SEPARATOR}${item.value}`
    );

    const header = currentColumns
      .map((c) => translatePolicyKey(c.name))
      .join(SEPARATOR);

    const rows = rowsToExport.map((data) =>
      currentColumns
        .map((c) => {
          const value = data[c.id] ?? "";
          const stringValue = String(value);
          return stringValue.includes(SEPARATOR) ||
            stringValue.includes('"') ||
            stringValue.includes("\n")
            ? `"${stringValue.replace(/"/g, '""')}"`
            : stringValue;
        })
        .join(SEPARATOR)
    );

    const contents = [
      ...metadataRows,
      "",
      header,
      ...rows,
      "",
      ...footerRows,
    ].join("\n");

    const blob = new Blob(["\uFEFF" + contents], {
      type: "text/csv;charset=utf-8;",
    });

    downloadBlob(blob, csvFilename);
  };

  const handlePrint = async () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const currentColumns = gridRef.current.visibleColumns.filter(
      (c) => c.name !== "selected" && c.name !== "__rowNumber__"
    );
    const filteredRows = gridRef.current?.data || filteredDataSource;
    const rows = getPrintRows ? getPrintRows(filteredRows) : filteredRows;

    const tableHeader = currentColumns
      .map((c) => `<th>${translatePolicyKey(c.name || c.header || "")}</th>`)
      .join("");

    const tableRows = rows
      .map(
        (row: any, index: number) => `
        <tr>
          <td style="color:#8a8a8a;text-align:center;font-weight:700;">${
            index + 1
          }</td>
          ${currentColumns
            .map((col) => `<td>${row[col.name] ?? ""}</td>`)
            .join("")}
        </tr>
      `
      )
      .join("");

    const metadataHTML = (printReportMetadata ?? [])
      .map(
        (item) => `
        <div class="meta-row">
          ${item.label ? `<span class="meta-label">${item.label}:</span>` : ""}
          <span class="meta-value ${!item.label ? "meta-value--full" : ""}">${
          item.value
        }</span>
        </div>
      `
      )
      .join("");

    const footerMetadataHTML =
      (printReportFooterMetadata ?? []).length > 0
        ? `
        <thead>
          <tr>
            ${(printReportFooterMetadata ?? [])
              .map((item) => `<th>${item.label ?? ""}</th>`)
              .join("")}
          </tr>
        </thead>
        <tbody>
          <tr>
            ${(printReportFooterMetadata ?? [])
              .map((item) => `<td>${item.value}</td>`)
              .join("")}
          </tr>
        </tbody>
      `
        : "";

    const logoHTML = logoUrl
      ? `<img class="page-header__logo-img" src="${logoUrl}" alt="Logo" />`
      : `<div class="page-header__logo-fallback">L</div>`;

    const html = `
      <html>
        <head>
          <title>${gridTitle}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }

            body {
              font-family: 'Inter', Arial, sans-serif;
              background: #fff;
              color: #1a1a1a;
              font-size: 10px;
              font-weight: 500;
              word-break: break-word;
            }

            /* ── PAGE HEADER ── */
            .page-header {
              background: #fff;
              border-bottom: 4px solid #8a1d06;
              padding:  10px 16px;
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 16px;
            }

            .page-header__left {
              display: flex;
              align-items: center;
              gap: 14px;
            }

            .page-header__logo-img {
              width: 48px;
              height: 48px;
              object-fit: contain;
              display: block;
            }

            .page-header__logo-fallback {
              width: 48px;
              height: 48px;
              background: #8a1d06;
              border-radius: 6px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 22px;
              font-weight: 700;
              color: #fff;
              flex-shrink: 0;
            }

            .page-header__divider {
              width: 1px;
              height: 40px;
              background: #e5e7eb;
              flex-shrink: 0;
            }

            .page-header__title {
              font-size: 17px;
              font-weight: 700;
              color: #1a1a1a;
              letter-spacing: -0.2px;
              line-height: 1.2;
            }

            .page-header__subtitle {
              font-size: 9.5px;
              color: #606367;
              margin-top: 3px;
              letter-spacing: 0.5px;
              text-transform: uppercase;
              font-weight: 500;
            }

            .page-header__badge {
              border: 1.5px solid #8a1d06;
              color: #8a1d06;
              font-size: 9px;
              font-weight: 700;
              letter-spacing: 1px;
              text-transform: uppercase;
              padding: 10px 10px;
              border-radius: 3px;
              white-space: nowrap;
              align-self: flex-start;
            }

            /* ── METADATA BLOCK ── */
            .meta-block {
              margin: 18px 24px;
              padding: 12px 16px;
              background: #fff8f7;
              border: 1px solid #f0d5d0;
              border-left: 4px solid #8a1d06;
              border-radius: 0 4px 4px 0;
            }

            .meta-grid {
              display: flex;
              flex-wrap: wrap;
              gap: 6px 24px;
            }

            .meta-row {
              display: flex;
              align-items: baseline;
              gap: 6px;
              flex: 0 1 auto;
              white-space: nowrap;
            }

            .meta-label {
              font-size: 10px;
              font-weight: 600;
              color: #8a1d06;
              text-transform: uppercase;
              letter-spacing: 0.4px;
            }

            .meta-value {
              font-size: 12px;
              color: #1a1a1a;
              font-weight: 700;
            }

            .meta-value--full {
              font-size: 12px;
              color: #1a1a1a;
              font-weight: 700;
            }

            /* ── SECTION DIVIDER ── */
            .section-divider {
              height: 1px;
              background: linear-gradient(
                to right,
                #8a1d06 0%,
                #f0d5d0 40%,
                transparent 100%
              );
              margin: 0 24px 16px;
            }

            /* ── TABLE ── */
            .table-wrap {
              padding: 0 16px;
              width: 100%;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: auto;
            }

            thead tr {
              background: #8a1d06;
              color: #fff;
            }

            th {
              padding: 2px 3px;
              font-size: 9px;
              font-weight: 700;
              text-align: left;
              letter-spacing: 0.4px;
              text-transform: uppercase;
              border: none;
              border-bottom: 4px solid #fff;
              border-right: 1px solid rgba(255, 255, 255, 0.25);
            }

            th:last-child {
              border-right: none;
            }
            
            td {
              padding: 3px 3px;
              font-size: 9px;
              font-weight: 500;
              color: #2d2d2d;
              border-right: 1px solid #c9b3b0;
              white-space: nowrap;
            }
            
            td:last-child {
               border-right: none;
            }

            tbody tr:nth-child(even) td {
              background: #fff8f7;
            }

            tbody tr:last-child td {
              border-bottom: none;
            }

            /* ── FOOTER ── */
            .page-footer {
              margin: 16px 24px 0;
              padding-top: 12px;
              border-top: 3px solid #8a1d06;
            }

            .footer-table {
              width: 100%;
              border-collapse: collapse;
            }

            .footer-table thead tr {
              background: transparent;
              color: #8a1d06;
            }

            .footer-table th {
              font-size: 10px;
              font-weight: 700;
              color: #8a1d06;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              padding: 2px 12px 4px 0;
              border-bottom: 1px solid #f0d5d0;
              margin-bottom: 4px;
              white-space: nowrap;
              background: transparent;
              text-align: left;
            }

            .footer-table td {
              font-size: 12px;
              font-weight: 700;
              color: #1a1a1a;
              padding: 16px 12px 0 0;
              border-bottom: 1px solid #aaa;
              white-space: nowrap;
              background: transparent;
              min-width: 120px;
            }

            /* ── DOC SEAL ── */
            .doc-seal {
              display: flex;
              align-items: center;
              margin: 10px 24px 10px;
            }

            .doc-seal__line {
              flex: 1;
              height: 1px;
              background: linear-gradient(
                to right,
                transparent,
                #8a1d06 40%,
                #8a1d06 60%,
                transparent
              );
            }

            .doc-seal__diamond {
              width: 7px;
              height: 7px;
              background: #8a1d06;
              transform: rotate(45deg);
              flex-shrink: 0;
              margin: 0 8px;
            }

            /* ── PAGE BASELINE ── */
            .page-baseline {
              margin: 6px 24px 0;
              padding-top: 8px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding-bottom: 10px;
            }

            .page-baseline__text {
              font-size: 12px;
              color: #71717a;
              letter-spacing: 0.3px;
            }

            .page-baseline__dot {
              width: 4px;
              height: 4px;
              background: #8a1d06;
              border-radius: 50%;
              opacity: 0.7;
            }

            @page {
              margin: 8mm;
            }

            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }

              .page-footer,
              .page-baseline,
              .doc-seal {
                page-break-inside: avoid;
                break-inside: avoid;
              }

              table {
                page-break-inside: auto;
              }

              tr {
                page-break-inside: avoid;
                break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="page-header">
            <div class="page-header__left">
              ${logoHTML}
              <div class="page-header__divider"></div>
              <div>
                <div class="page-header__title">${gridTitle}</div>
                <div class="page-header__subtitle">Libra | lbrbroker.com</div>
              
                <div class="page-header__subtitle">${new Date().toLocaleDateString(
                  "bg-BG"
                )}</div>
              </div>
            </div>
            <div class="page-header__badge">Официален документ</div>
          </div>

          ${
            metadataHTML
              ? `<div class="meta-block"><div class="meta-grid">${metadataHTML}</div></div>`
              : ""
          }

          <div class="section-divider"></div>

          <div class="table-wrap">
            <table>
              <thead><tr><th style="width:32px;text-align:center;padding:12px 2px;">#</th>${tableHeader}</tr></thead>
              <tbody>${tableRows}</tbody>
            </table>
          </div>

          ${
            footerMetadataHTML
              ? `
            <div class="page-footer">
              <table class="footer-table">${footerMetadataHTML}</table>
            </div>
          `
              : ""
          }

          <div class="doc-seal">
            <div class="doc-seal__line"></div>
            <div class="doc-seal__diamond"></div>
            <div class="doc-seal__line"></div>
          </div>

          <div class="page-baseline">
            <span class="page-baseline__text">Libra | lbrbroker.com</span>
            <div class="page-baseline__dot"></div>
            <span class="page-baseline__text">Генерирано: ${new Date().toLocaleString(
              "bg-BG"
            )}</span>
          </div>

          <script>
            window.onload = () => {
              window.print();
              window.close();
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const downloadBlob = (blob: Blob, fileName = "grid-data.csv") => {
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.position = "absolute";
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleSearch = () => {
    setIsSearchVisible(!isSearchVisible);
    if (isSearchVisible) setFilterText("");
  };

  const overlayStyles = {
    position: "fixed" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 9998,
  };

  const modalStyles = {
    position: "fixed" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "95vw",
    height: "95vh",
    backgroundColor: colorMode === "dark" ? "#1f2937" : "#ffffff",
    zIndex: 9999,
    overflow: "auto",
    borderRadius: "8px",
  };

  const renderToolbarButtons = (fullscreen = false) => (
    <div className="flex items-center gap-1">
      {shouldShowAddAndDeleteButtons && (
        <>
          <button
            className="bg-primary flex h-6 w-6 items-center justify-center rounded text-white hover:bg-opacity-90"
            onClick={onAdd}
            title="Добави"
          >
            <i className="fa-solid fa-plus text-xs" />
          </button>
          <button
            className="bg-primary flex h-6 w-6 items-center justify-center rounded text-white hover:bg-opacity-90"
            onClick={onDelete}
            title="Изтрий"
          >
            <i className="fa-solid fa-minus text-xs" />
          </button>
        </>
      )}
      <button
        className="bg-primary flex h-6 w-6 items-center justify-center rounded text-white hover:bg-opacity-90"
        onClick={toggleSearch}
        title="Търсене"
      >
        <i className="fa-solid fa-search text-xs" />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isSearchVisible ? "ml-1 w-36 opacity-100" : "w-0 opacity-0"
        }`}
      >
        <input
          type="text"
          placeholder="Филтрирай..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="dark:border-form-strokedark dark:bg-form-input w-full rounded border px-2 py-0.5 text-xs text-black outline-none dark:text-white"
        />
      </div>
      <button
        className="bg-primary flex h-6 w-6 items-center justify-center rounded text-white hover:bg-opacity-90"
        onClick={onExportCSV ?? exportCSV}
        title="Свалете CSV"
      >
        <i className="fa-solid fa-download text-xs" />
      </button>
      <button
        className="bg-primary flex h-6 w-6 items-center justify-center rounded text-white hover:bg-opacity-90"
        onClick={handlePrint}
        title="Печат"
      >
        <i className="fa-solid fa-print text-xs" />
      </button>
      {!fullscreen ? (
        <button
          className="bg-primary flex h-6 w-6 items-center justify-center rounded text-white hover:bg-opacity-90"
          onClick={() => setIsFullscreen(true)}
          title="Разширяване"
        >
          <i className="fa-solid fa-expand text-xs" />
        </button>
      ) : (
        <button
          className="bg-primary flex h-6 w-6 items-center justify-center rounded text-white hover:bg-opacity-90"
          onClick={() => setIsFullscreen(false)}
          title="Затвори"
        >
          <i className="fa-solid fa-compress text-xs" />
        </button>
      )}
    </div>
  );
  return (
    <>
      <style>{`
        /* --- Strict 12px Bold Layout for Headers, Content Cells, and Inputs --- */
        .inovua-grid-small .InovuaReactDataGrid__column-header__text,
        .inovua-grid-small .InovuaReactDataGrid__cell__content,
        .inovua-grid-small .InovuaReactDataGrid__cell,
        .inovua-grid-small .InovuaReactDataGrid__header,
        .inovua-grid-small .inovua-react-toolkit-combo-box__value__display-value,
        .inovua-grid-small .InovuaReactDataGrid__cell-input {
          font-size: 12px !important;
          font-weight: 700 !important;
        }

        /* Target headers to force left-padding to 2px */
        .inovua-grid-small .InovuaReactDataGrid__column-header,
        .inovua-grid-small .InovuaReactDataGrid__column-header__content {
          padding-left: 2px !important;
        }
        
        .inovua-grid-small .InovuaReactDataGrid__footer,
        .inovua-grid-small .InovuaReactDataGrid__pagination-toolbar,
        .inovua-grid-small .inovua-react-pagination-toolbar {
          font-size: 10px !important;
          min-height: 32px !important;
          height: 32px !important;
          line-height: 32px !important;
          padding: 0 8px !important;
          box-sizing: border-box !important;
          display: flex !important;
          align-items: center !important;
        }
        .inovua-grid-small .inovua-react-toolkit-toolbar__inner-wrapper,
        .inovua-grid-small .inovua-react-pagination-toolbar__text,
        .inovua-grid-small .inovua-react-pagination-toolbar__per-page-text,
        .inovua-grid-small .inovua-react-pagination-toolbar__page-text,
        .inovua-grid-small .inovua-react-pagination-toolbar__page-count-text,
        .inovua-grid-small .inovua-react-toolkit-toolbar__spacer,
        .inovua-grid-small .inovua-react-toolkit-toolbar__separator {
          font-size: 10px !important;
          line-height: normal !important;
        }
        .inovua-grid-small .inovua-react-pagination-toolbar__current-page,
        .inovua-grid-small .inovua-react-toolkit-numeric-input {
          min-width: 44px !important;
          height: 28px !important;
          min-height: 28px !important;
          font-size: 10px !important;
          font-weight: 700 !important;
        }
        .inovua-grid-small .inovua-react-toolkit-numeric-input__input {
          font-size: 10px !important;
          height: 20px !important;
          padding: 0 2px !important;
        }
        .inovua-grid-small .inovua-react-pagination-toolbar__page-size-combo,
        .inovua-grid-small .inovua-react-toolkit-combo-box {
          text-transform: capitalize !important;
          min-width: 80px !important;
          width: 80px !important;
          height: 28px !important;
          min-height: 28px !important;
          font-size: 10px !important;
          line-height: 20px !important;
          pointer-events: none !important;
          opacity: 0.5 !important; 
        }
        .inovua-grid-small .inovua-react-toolkit-combo-box__value {
          font-size: 10px !important;
          line-height: 20px !important;
          height: 20px !important;
        }
        .inovua-grid-small .inovua-react-toolkit-combo-box__input {
          font-size: 10px !important;
          height: 20px !important;
        }
        .inovua-grid-small .inovua-react-pagination-toolbar__icon .inovua-react-toolkit-button__icon-wrap svg {
          width: 14px !important;
          height: 14px !important;
        }
      `}</style>
      {!isFullscreen ? (
        <div className="inovua-grid-small border-stroke dark:border-strokedark dark:bg-boxdark mt-2 overflow-hidden rounded-sm border bg-white shadow-sm">
          <div className="border-stroke dark:border-strokedark flex items-center justify-between border-b px-3 py-2">
            <h3 className="text-xs font-bold text-black dark:text-white">
              {gridTitle}
            </h3>
            {renderToolbarButtons(false)}
          </div>
          <ReactDataGrid
            handle={setGridRef}
            style={{ minHeight: gridHeight, boxShadow: 4 }}
            rowHeight={28}
            headerHeight={30}
            loading={isLoading}
            idProperty={idProperty}
            columns={selectableColumns}
            onRowClick={(row) => onRowClick(row)}
            editable={editable}
            fillEmptySpace
            autoSizeColumnsOnRender
            enableColumnFilterContextMenu={false}
            showColumnMenuFilterOptions={false}
            showColumnMenuSortOptions={false}
            showColumnMenuGroupOptions={false}
            showColumnMenuLockOptions={false}
            showColumnMenuTool={false}
            multiSort
            allowUnsort
            onEditComplete={onEditComplete}
            theme={theme}
            i18n={i18n}
            dataSource={filteredDataSource}
            pagination
            sortInfo={sortInfo}
            onSortInfoChange={(info) =>
              setSortInfo(Array.isArray(info) ? info : info ? [info] : [])
            }
            defaultLimit={10000}
            pageSizes={[10000]}
            loadingText={Translations.LOADING}
            emptyText={Translations.NO_DATA}
          />
        </div>
      ) : (
        <>
          <div style={overlayStyles} onClick={() => setIsFullscreen(false)} />
          <div style={modalStyles}>
            <div className="border-stroke dark:border-strokedark flex items-center justify-between border-b px-3 py-2">
              <h3 className="text-xs font-bold text-black dark:text-white">
                {gridTitle}
              </h3>
              {renderToolbarButtons(true)}
            </div>
            <ReactDataGrid
              handle={setGridRef}
              style={{
                height: "calc(100% - 52px)",
                boxShadow: 4,
                overflowX: "auto",
                margin: "8px",
              }}
              rowHeight={28}
              headerHeight={30}
              loading={isLoading}
              fillEmptySpace
              autoSizeColumnsOnRender
              idProperty={idProperty}
              columns={selectableColumns}
              onRowClick={(row) => onRowClick(row)}
              editable={editable}
              onEditComplete={onEditComplete}
              theme={theme}
              i18n={i18n}
              multiSort
              allowUnsort
              sortInfo={sortInfo}
              onSortInfoChange={(info) =>
                setSortInfo(Array.isArray(info) ? info : info ? [info] : [])
              }
              dataSource={filteredDataSource}
              pagination={true}
              loadingText={Translations.LOADING}
              emptyText={Translations.NO_DATA}
              enableKeyboardNavigation={true}
              enableTreeRowReorder={false}
            />
          </div>
        </>
      )}
    </>
  );
};

export default DataGrid;
